import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useDispatch } from 'react-redux'
import { adminChangePasswordService, adminPreferenceService, getAdminSessionService, getProfileService, logoutAdminSessionService, logoutAllAdminSessionService, regenrateBackupCodeService, resetMfaService } from '../service'
import { setTotalItemPerpage, setUserProfile } from '../../../redux/slices/authSlice'

const useProfile = () => {
  const queryClient = useQueryClient()
  const dispatch = useDispatch()
  const { mutateAsync: handleAdminChangePassword, isPending: changePasswordPending } = useMutation({
    mutationFn: ({ oldPassword, newPassword, mfaCode }: { oldPassword: string, newPassword: string, mfaCode: string }) => adminChangePasswordService({
      oldPassword,
      newPassword,
      mfaCode,
    }),
    mutationKey: ['change-password']
  })

  const { mutateAsync: handleAdminPreference, isPending: preferencePending } = useMutation({
    mutationFn: ({ sendLoginNotification }: { sendLoginNotification: boolean }) => adminPreferenceService({
      sendLoginNotification
    }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['profile-data'] })
    },
    mutationKey: ['admin-preference']
  })


  const { data: profileData, isLoading: isLoadingProfileData, refetch: refetchProfileData } = useQuery({
    queryKey: ["profile-data"],
    queryFn: () => getProfileService(),
  })

  useEffect(() => {
    if (!profileData) {
      return
    }

    dispatch(setUserProfile(profileData))
    if (profileData.totalItemPerpage) {
      dispatch(setTotalItemPerpage(profileData.totalItemPerpage))
    }
  }, [dispatch, profileData])

  const { data: adminSessionData, isLoading: isLoadingAdminSessionData } = useQuery({
    queryKey: ["get-admin-session"],
    queryFn: () => getAdminSessionService(),
  })

  const { mutateAsync: handleRegenrateBackupCode, isPending: regenrateBackupCodePending } = useMutation({
    mutationFn: (mfaCode: string) => regenrateBackupCodeService({ mfaCode }),
    mutationKey: ['regenerate-mfa-code']
  })

  const { mutateAsync: handleLogoutAllAdminSession, isPending: logoutAllAdminSessionPending } = useMutation({
    mutationFn: () => logoutAllAdminSessionService(),
    mutationKey: ['logout-all-admin-session']
  })


  const { mutateAsync: handleLogoutAdminSession, isPending: logoutAdminSessionPending } = useMutation({
    mutationFn: (sessionId: string) => logoutAdminSessionService(sessionId),
    mutationKey: ['logout-admin-session']
  })

  const { mutateAsync: handleResetMfa, isPending: resetMfaPending } = useMutation({
    mutationFn: (mfaCode: string) => resetMfaService({ mfaCode }),
    mutationKey: ['reset-mfa']
  })

  return (
    {
      handleAdminChangePassword,
      changePasswordPending,
      handleAdminPreference,
      preferencePending,
      profileData,
      isLoadingProfileData,
      refetchProfileData,
      adminSessionData,
      isLoadingAdminSessionData,
      handleRegenrateBackupCode,
      regenrateBackupCodePending,
      handleLogoutAllAdminSession,
      logoutAllAdminSessionPending,
      handleLogoutAdminSession,
      logoutAdminSessionPending,
      handleResetMfa,
      resetMfaPending
    }
  )
}

export default useProfile
